import { LogSearchParam, QuickSearchParam } from '@@types/Api/Log';
import Channel from '@@types/Channel';
import { CHANNEL_ID, LOG_ID } from '@@types/common';
import Member from '@@types/Member';

import { CHANNEL_MEMBER_SEARCH_KEYWORD_MAX_LEN, MAIN_THREAD_CHANNEL_ID, PANEL_TYPE } from '~/constants';
import i18n from '~/i18n';
import { makeDummyChannel, sortByUpdatedAt } from '~/services/channelService';
import { channelsStore } from '~/stores/ChannelsStore';
import { globalSettingStore } from '~/stores/GlobalSettingStore';
import { api } from '~/stores/providers/Api2';
import { searchStore } from '~/stores/SearchStore';
import { userStore } from '~/stores/UserStore';
import { includesInsensitiveNonCurry } from '~/utils/fnUtil/fnUtil';
import { isLeaver } from '~/utils/memberUtil';
import { debounce, isEqual, isString } from 'lodash';
import moment from 'moment';
import {
  append,
  dissoc,
  equals,
  filter,
  flatten,
  includes,
  map,
  pipe,
  pluck,
  prepend,
  prop,
  propOr,
  propSatisfies,
  reject,
  tap,
  uniqBy,
  when,
} from 'ramda';

import Actions from '../Actions';

class SearchActions {
  public showInvalidKeywordError = debounce(
    (text: string) => {
      if (!!text && isString(text)) {
        this.actions.notificationActions.showError({ text });
      }
    },
    5000,
    { leading: true, trailing: false }
  );

  constructor(private actions: Actions) {}

  public searchLogs = () => {
    const { trimedSearchKeyword, searchParams } = searchStore;

    const params = this.getLogSearchParams();
    if (isEqual(params, searchParams)) {
      return Promise.reject();
    }

    searchStore.setSearchParams(params);
    return this.callSearch(PANEL_TYPE.LOG, () => this.searchInnerLogsOrMentions(params)).then(
      tap(() => {
        searchStore.setSearchedKeyword(trimedSearchKeyword);
      })
    );
  };

  public searchMentions = () => {
    return this.callSearch(PANEL_TYPE.MENTION, () => this.searchInnerLogsOrMentions(this.getMentionSearchParams()));
  };

  public searchLinks = () => {
    return this.callSearch(PANEL_TYPE.LINK, () => this.quickSearchInner(this.getLinkSearchParams()));
  };

  public searchFiles = () => {
    return this.callSearch(PANEL_TYPE.FILE, () => this.quickSearchInner(this.getFileSearchParams()));
  };

  public searchChannelInviteMembers = (params: { keyword: string; page: number }) => {
    return this.validateInviteChannelSearchKeyword(params.keyword).then(() => this.searchMembersWithTotalCount(params));
  };

  public searchAndFilterRecentChannels = (keyword: string): Promise<Partial<Channel>[]> => {
    return this.validateChannelOrMemberSearchKeyword(keyword)
      .then((keyword) => {
        return Promise.all([this.filterTopicChannels(keyword), this.getChannelsWithSearchedMembers(keyword)]);
      })
      .then(flatten)
      .then((channels: Partial<Channel>[]) => {
        return pipe<any, any, any>(uniqBy(prop('channelId')), sortByUpdatedAt)(channels) as Partial<Channel>[];
      });
  };

  public searchMembers = (params: { keyword: string; boostMembersId?: string[] }): Promise<Member[]> => {
    return api.member.searchMembers(params).then(pluck('member')).then(tap(this.fetchSearchedMembers));
  };

  private getLogSearchParams = (): LogSearchParam => {
    const { channelId, from, trimedSearchKeyword, memberId, startDate, endDate, searchType } = searchStore;
    return when(
      propSatisfies(equals('all'), 'sentMemberIds'),
      dissoc('sentMemberIds'),
      Object.assign(
        {
          channelId,
          from,
          size: 20,
          queries: trimedSearchKeyword,
          sentMemberIds: memberId,
          searchType,
        },
        startDate ? { startDate: moment(startDate).format('YYYY-MM-DD') } : {},
        endDate ? { endDate: moment(endDate).format('YYYY-MM-DD') } : {}
      )
    );
  };

  private getMentionSearchParams = (): LogSearchParam => {
    return {
      channelId: searchStore.channelId,
      from: searchStore.from,
      mentionType: searchStore.mentionType,
      searchType: searchStore.searchType,
      size: 20,
    };
  };

  private searchInnerLogsOrMentions = (params: LogSearchParam) => {
    if (params.channelId === 'all') {
      return api.log.searchLogs(params);
    } else {
      return api.log.searchLogsInChannel(params);
    }
  };

  private getLinkSearchParams = (): QuickSearchParam => {
    return {
      channelId: searchStore.channelId,
      from: searchStore.from,
      size: 20,
      type: 'linkAll',
    };
  };

  private getFileSearchParams = (): QuickSearchParam => {
    return when(propSatisfies(equals('all'), 'fileType'), dissoc('fileType'), {
      channelId: searchStore.channelId,
      from: searchStore.from,
      fileType: searchStore.fileType,
      size: 20,
      type: 'fileAll',
    });
  };

  private callSearch = <T extends { contents: any[]; totalCount: number }>(
    panelType: PANEL_TYPE,
    call: () => Promise<T>
  ) => {
    searchStore.resetData();
    searchStore.setIsSearching(true);
    searchStore.setSearchedFor(panelType);

    return call()
      .then((result) => {
        if (searchStore.searchedFor !== panelType) {
          return Promise.reject();
        }
        return result;
      })
      .then(searchStore.setSearchedLogs)
      .finally(() => {
        searchStore.setIsSearching(false);
      });
  };

  private quickSearchInner = (params: QuickSearchParam) => {
    if (params.channelId === 'all') {
      return api.log.searchQuickSearch(params);
    } else {
      return api.log.searchQuickSearchInChannel(params);
    }
  };

  private validateInviteChannelSearchKeyword = (keyword: string) => {
    return keyword.length > CHANNEL_MEMBER_SEARCH_KEYWORD_MAX_LEN
      ? Promise.reject(
          i18n.t('limitKeywordLength', {
            max: CHANNEL_MEMBER_SEARCH_KEYWORD_MAX_LEN,
          })
        )
      : Promise.resolve(keyword);
  };

  private searchMembersWithTotalCount = (params: { keyword: string; page: number }) => {
    return api.member
      .searchMembersWithTotalCount(params)
      .then(tap(pipe(propOr([], 'contents'), pluck('member'), this.fetchSearchedMembers)));
  };

  private validateChannelOrMemberSearchKeyword = (keyword: string): Promise<string> => {
    if (keyword.length === 0) {
      return Promise.reject('');
    }
    return keyword.length > CHANNEL_MEMBER_SEARCH_KEYWORD_MAX_LEN
      ? Promise.reject(
          i18n.t('limitKeywordLength', {
            max: CHANNEL_MEMBER_SEARCH_KEYWORD_MAX_LEN,
          })
        )
      : Promise.resolve(keyword);
  };

  private filterTopicChannels = (keyword: string): Promise<Partial<Channel>[]> => {
    const includeDoorayNews =
      globalSettingStore.isDoorayNewsOn &&
      // channelsStore.doorayNewsChannel &&
      includesInsensitiveNonCurry(keyword, channelsStore.doorayNewsChannel.title);
    const includeThreadMainChannel = includesInsensitiveNonCurry(keyword, i18n.t('thread', '글타래'));
    let filteredTopicChannels = filter(
      (channel) => includesInsensitiveNonCurry(keyword, channel.title),
      channelsStore.topicChannels
    );
    filteredTopicChannels = includeThreadMainChannel
      ? prepend(channelsStore.getById(MAIN_THREAD_CHANNEL_ID), filteredTopicChannels)
      : filteredTopicChannels;
    filteredTopicChannels = includeDoorayNews
      ? prepend(channelsStore.doorayNewsChannel, filteredTopicChannels)
      : filteredTopicChannels;
    return Promise.resolve(filteredTopicChannels);
  };

  private getChannelsWithSearchedMembers = (keyword: string): Promise<Partial<Channel>[]> => {
    return this.searchMembers({ keyword }).then(reject(isLeaver)).then(this.getChannelOrCreateDummyChannelByMembers);
  };

  private getChannelOrCreateDummyChannelByMembers = (members: Member[]): Partial<Channel>[] => {
    return flatten(map(this.findChannelsInMember, members));
  };

  private findChannelsInMember = (member: Member): Partial<Channel>[] => {
    if (member.id === userStore.id) {
      return [channelsStore.meChannel];
    }
    const groupChannels = this.filterGroupChannelsInMember(member);
    const directChannel = this.findDirectChannelOrCreateDummyChannel(member);
    return append(directChannel, groupChannels);
  };

  private filterGroupChannelsInMember = (member: Member) => {
    return filter((channel: Channel) => {
      return includes(member.id, channel.members);
    }, channelsStore.nonTopicChannels);
  };

  private findDirectChannelOrCreateDummyChannel = (member: Member) => {
    const channel = channelsStore.getByOpponentId(member.id);
    return !channel ? makeDummyChannel(member.id, userStore.id) : channel;
  };

  private fetchSearchedMembers = (members) => {
    return pipe(pluck('id'), this.actions.memberActions.fetchBulkMembers)(members);
  };

  deleteCheckedLogs = async () => {
    const selectedKeys = Object.keys(searchStore.searchedCheckedMap).filter(
      (key) => !!searchStore.searchedCheckedMap[key]
    );
    await this.actions.modalActions.openConfirm({
      description: i18n.t('filePanel.removeFiles.message', {
        fileCount: selectedKeys.length,
        defaultValue:
          '{{fileCount}}개의 파일이 파일이 선택되었습니다. 파일을 삭제하면 다시 복구할 수 없습니다. 삭제 하시곘습니까?',
      }),
    });
    const logs: Array<{
      logId: LOG_ID;
      channelId: CHANNEL_ID;
    }> = selectedKeys.map((logId) => ({
      logId,
      channelId: searchStore.searchedLogMap[logId]?.channelId ?? '',
    }));
    await api.log.deleteLogs(channelsStore.meChannel.channelId, logs);
    await this.searchFiles();
  };
}

export default SearchActions;
